import React, { useState } from "react";
import styled from "styled-components";
import ChevronRight from "@/assets/svg/chevronRight.svg";
import Timeline from "./Timeline";
import ViewImageIcon from "@/assets/svg/viewImage.svg";
import TimelineLightbox from "./TimelineLightbox";
import { VideoFrame } from "../types";

const BottomLayerContainer = styled.div`
  justify-content: flex-start;
  align-items: flex-start;
  flex-direction: column;
  padding: 4px 0;
  width: 100%;
  gap: 16px;
  display: flex;
`;

const NavigationButtons = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
  padding: 0 20px;
`;

const NavigationButton = styled.div<{ $disabled?: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 0.5px solid rgba(62, 62, 68, 0.6);
  background: rgba(40, 40, 44, 0.797);
  backdrop-filter: blur(10px);
  display: flex;
  justify-content: center;
  align-items: center;
  color: #f7f8f8;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.$disabled ? 0.4 : 1)};
  transition: opacity 0.2s ease;

  svg {
    width: 16px;
    height: 16px;
    color: #f7f8f8;
  }

  ${(props) =>
    props.$disabled &&
    `
    pointer-events: none;
  `}
`;

interface BottomLayerProps {
  videos: VideoFrame[];
  currentVideoIndex: number;
  previousVideoIndex: number;
  targetVideoIndex: number;
  transitionDurationMs: number;
  framesPath: string;
  onVideoSelect: (index: number) => void;
  isTransitioning: boolean;
  disabled?: boolean;
  preloadStatus: any;
  navigateNext: () => void;
  navigatePrev: () => void;
}

export default function BottomLayer({
  videos,
  currentVideoIndex,
  previousVideoIndex,
  targetVideoIndex,
  transitionDurationMs,
  framesPath,
  onVideoSelect,
  isTransitioning,
  disabled = false,
  preloadStatus,
  navigateNext,
  navigatePrev,
}: BottomLayerProps) {
  const [displayLightbox, setDisplayLightbox] = useState(false);

  const currentVideo = videos[currentVideoIndex];

  // Check if navigation buttons should be disabled
  const getNextVideoIndex = () => (currentVideoIndex + 1) % videos.length;
  const getPrevVideoIndex = () => (currentVideoIndex - 1 + videos.length) % videos.length;

  const nextVideoIndex = getNextVideoIndex();
  const prevVideoIndex = getPrevVideoIndex();

  // Check if target videos are navigable
  const isNextNavigable = preloadStatus?.videoStates?.[nextVideoIndex]?.loadedFrames > 0 || false;
  const isPrevNavigable = preloadStatus?.videoStates?.[prevVideoIndex]?.loadedFrames > 0 || false;

  // Disable next button if next video isn't loaded
  const isNextDisabled = disabled || isTransitioning || !isNextNavigable;
  // Disable prev button if prev video isn't loaded
  const isPrevDisabled = disabled || isTransitioning || !isPrevNavigable;

  const openLightbox = (video: VideoFrame) => {
    setDisplayLightbox(true);
  };

  return (
    <>
      {displayLightbox && (
        <TimelineLightbox currentVideo={currentVideo} framesPath={framesPath} setDisplayLightbox={setDisplayLightbox} />
      )}
      <BottomLayerContainer>
        <NavigationButtons>
          <NavigationButton
            style={{ transform: "rotate(180deg)" }}
            onClick={isPrevDisabled ? undefined : navigatePrev}
            $disabled={isPrevDisabled}>
            <ChevronRight />
          </NavigationButton>
          <NavigationButton onClick={isNextDisabled ? undefined : navigateNext} $disabled={isNextDisabled}>
            <ChevronRight />
          </NavigationButton>
          <NavigationButton onClick={() => openLightbox(currentVideo)}>
            <ViewImageIcon fill='#f7f8f8e5' />
          </NavigationButton>
        </NavigationButtons>
        <Timeline
          videos={videos}
          currentVideoIndex={currentVideoIndex}
          previousVideoIndex={previousVideoIndex}
          targetVideoIndex={targetVideoIndex}
          transitionDurationMs={transitionDurationMs}
          framesPath={framesPath}
          onVideoSelect={onVideoSelect}
          isTransitioning={isTransitioning}
          preloadStatus={preloadStatus}
        />
      </BottomLayerContainer>
    </>
  );
}
